import React, { useState, useEffect, useMemo } from 'react';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../../../components/ui/Modal';
import { Button, AlertDialog } from '../../../components/ui';
import { useEmailNotifications } from '../hooks/useApprovalRequests';
import { API_BASE_URL } from '../../../config/apiConfig';
import {
  EnvelopeIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  RefreshIcon,
} from '../../../components/ui/Icons';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';

const TYPE_LABELS = {
  initial: 'Initial',
  reminder: 'Reminder',
  status_update: 'Status Update',
  rejection: 'Rejection',
  approval: 'Approval',
};

const TYPE_STYLES = {
  initial: 'bg-blue-50 text-blue-700 ring-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:ring-blue-800',
  reminder: 'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:ring-amber-800',
  status_update: 'bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700',
  rejection: 'bg-red-50 text-red-700 ring-red-200 dark:bg-red-900/30 dark:text-red-300 dark:ring-red-800',
  approval: 'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:ring-emerald-800',
};

const STATUS_META = {
  sent: {
    label: 'Accepted by SMTP',
    icon: CheckCircleIcon,
    className: 'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:ring-emerald-800',
    iconClass: 'text-emerald-500',
  },
  failed: {
    label: 'Failed',
    icon: XCircleIcon,
    className: 'bg-red-50 text-red-700 ring-red-200 dark:bg-red-900/30 dark:text-red-300 dark:ring-red-800',
    iconClass: 'text-red-500',
  },
  pending: {
    label: 'Pending',
    icon: ClockIcon,
    className: 'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:ring-amber-800',
    iconClass: 'text-amber-500',
  },
  bounced: {
    label: 'Bounced',
    icon: ExclamationTriangleIcon,
    className: 'bg-orange-50 text-orange-700 ring-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:ring-orange-800',
    iconClass: 'text-orange-500',
  },
};

const parseSmtpResponse = (raw) => {
  if (!raw) return null;
  try {
    return typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch {
    return { response: String(raw) };
  }
};

const EmailNotificationHistoryModal = ({ isOpen, onClose, approvalRequestId }) => {
  const { theme } = useTheme();
  const { notifications, isLoading, mutate } = useEmailNotifications(approvalRequestId);
  const [isSystemEnabled, setIsSystemEnabled] = useState(true);
  const [showAlert, setShowAlert] = useState({ show: false, message: '', type: 'error' });
  const [resendingId, setResendingId] = useState(null);
  const [previewNotification, setPreviewNotification] = useState(null);

  useEffect(() => {
    const readSettingBoolean = async (key) => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/application-setting/key/${key}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) return true;
      const setting = await response.json();
      return setting?.value?.toLowerCase() === 'true';
    };

    const checkSystemStatus = async () => {
      try {
        const [systemEnabled, approvalEnabled] = await Promise.all([
          readSettingBoolean('emailNotificationSystemEnabled'),
          readSettingBoolean('emailNotificationApprovalRequestsEnabled'),
        ]);
        setIsSystemEnabled(systemEnabled && approvalEnabled);
      } catch (error) {
        console.error('Error checking email notification system status:', error);
        setIsSystemEnabled(true);
      }
    };

    if (isOpen) {
      checkSystemStatus();
    }
  }, [isOpen]);

  const summary = useMemo(() => {
    const counts = { total: notifications.length, sent: 0, failed: 0, pending: 0, bounced: 0 };
    notifications.forEach((n) => {
      if (counts[n.status] !== undefined) counts[n.status] += 1;
    });
    return counts;
  }, [notifications]);

  const handleResend = async (notificationId) => {
    if (!isSystemEnabled) {
      setShowAlert({
        show: true,
        message: 'Cannot resend: Email notification system is disabled',
        type: 'warning',
      });
      return;
    }

    setResendingId(notificationId);

    try {
      const token = localStorage.getItem('authToken');
      const savedUser = localStorage.getItem('user');
      let userId = null;

      if (savedUser && savedUser !== 'undefined' && savedUser !== 'null') {
        try {
          const parsedUser = JSON.parse(savedUser);
          userId = parsedUser.id?.toString() || null;
        } catch (parseError) {
          userId = null;
        }
      }

      const headers = {
        'Content-Type': 'application/json',
        ...(userId && { 'user-id': userId }),
        ...(token && { Authorization: `Bearer ${token}` }),
      };

      const response = await fetch(
        `${API_BASE_URL}/approval-requests/${approvalRequestId}/email-notifications/${notificationId}/resend`,
        {
          method: 'POST',
          headers,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || `Failed to resend email: ${response.statusText}`);
      }

      setShowAlert({
        show: true,
        message: 'Email resent successfully',
        type: 'success',
      });
      mutate();
    } catch (error) {
      setShowAlert({
        show: true,
        message: error.message || 'Failed to resend email. Please try again.',
        type: 'error',
      });
    } finally {
      setResendingId(null);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Not sent';
    return new Date(date).toLocaleString(undefined, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getInitials = (name, email) => {
    const source = (name || email || '?').trim();
    const parts = source.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return source.slice(0, 2).toUpperCase();
  };

  if (!isOpen) return null;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalHeader onClose={onClose}>
          <div className="flex items-center gap-3">
            <div className={cn(
              'flex h-10 w-10 items-center justify-center rounded-xl',
              'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200'
            )}>
              <EnvelopeIcon className="h-5 w-5" />
            </div>
            <div>
              <ModalTitle>Email Notification History</ModalTitle>
              <p className={cn('mt-0.5 text-xs', theme.text?.muted || 'text-gray-500')}>
                Delivery log for this approval request
              </p>
            </div>
          </div>
        </ModalHeader>

        <ModalContent maxHeight="70vh">
          {!isSystemEnabled && (
            <div className="mb-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-800 dark:bg-amber-900/20">
              <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
              <p className="text-sm text-amber-800 dark:text-amber-200">
                Email notifications are currently disabled. History is view-only.
              </p>
            </div>
          )}

          {isLoading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16">
              <div className="h-9 w-9 animate-spin rounded-full border-2 border-slate-300 border-t-slate-700" />
              <p className="text-sm text-gray-500">Loading notifications�</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-gray-50/80 px-6 py-14 dark:border-gray-700 dark:bg-gray-900/40">
              <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-white shadow-sm ring-1 ring-gray-200 dark:bg-gray-800 dark:ring-gray-700">
                <EnvelopeIcon className="h-7 w-7 text-gray-400" />
              </div>
              <h3 className={cn('text-sm font-semibold', theme.text?.primary || 'text-gray-900')}>
                No email notifications yet
              </h3>
              <p className="mt-1 max-w-sm text-center text-sm text-gray-500">
                When emails are sent for this approval request, they will appear here with status and preview.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Summary chips */}
              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 ring-1 ring-inset ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700">
                  {summary.total} total
                </span>
                {summary.sent > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200">
                    <CheckCircleIcon className="h-3.5 w-3.5" />
                    {summary.sent} accepted by SMTP
                  </span>
                )}
                {summary.failed > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-3 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-200">
                    <XCircleIcon className="h-3.5 w-3.5" />
                    {summary.failed} failed
                  </span>
                )}
                {summary.pending > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-200">
                    <ClockIcon className="h-3.5 w-3.5" />
                    {summary.pending} pending
                  </span>
                )}
                {summary.bounced > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-orange-50 px-3 py-1 text-xs font-medium text-orange-700 ring-1 ring-inset ring-orange-200">
                    <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                    {summary.bounced} bounced
                  </span>
                )}
              </div>

              {/* Notification cards */}
              <div className="space-y-3">
                {notifications.map((notification) => {
                  const statusMeta = STATUS_META[notification.status] || STATUS_META.pending;
                  const StatusIcon = statusMeta.icon;
                  const displayName =
                    notification.recipient?.userDisplayName || notification.recipientEmail;
                  const typeLabel = TYPE_LABELS[notification.emailType] || notification.emailType;
                  const typeStyle = TYPE_STYLES[notification.emailType] || TYPE_STYLES.status_update;
                  const smtp = parseSmtpResponse(notification.smtpResponse);
                  const messageId = notification.smtpMessageId || smtp?.messageId;
                  const smtpServerResponse = smtp?.response;
                  const formatList = (list) =>
                    Array.isArray(list) && list.length > 0
                      ? list.map((item) => String(item)).join(', ')
                      : null;
                  const acceptedList = formatList(smtp?.accepted);
                  const rejectedList = formatList(smtp?.rejected);
                  const pendingList = formatList(smtp?.pending);
                  const hasSmtpDetails =
                    messageId ||
                    smtpServerResponse ||
                    acceptedList ||
                    rejectedList ||
                    pendingList;

                  return (
                    <div
                      key={notification.id}
                      className={cn(
                        'rounded-xl border bg-white p-4 shadow-sm transition-shadow hover:shadow-md',
                        'border-gray-200 dark:border-gray-700 dark:bg-gray-900/50'
                      )}
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="flex min-w-0 items-start gap-3">
                          <div className={cn(
                            'flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                            'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200'
                          )}>
                            {getInitials(displayName, notification.recipientEmail)}
                          </div>
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className={cn('truncate text-sm font-semibold', theme.text?.primary || 'text-gray-900')}>
                                {displayName}
                              </p>
                              <span className={cn(
                                'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset',
                                typeStyle
                              )}>
                                {typeLabel}
                              </span>
                              <span className={cn(
                                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset',
                                statusMeta.className
                              )}>
                                <StatusIcon className={cn('h-3.5 w-3.5', statusMeta.iconClass)} />
                                {statusMeta.label}
                              </span>
                            </div>
                            <p className="mt-0.5 truncate text-xs text-gray-500">
                              {notification.recipientEmail}
                            </p>
                            <p
                              className="mt-2 text-sm text-gray-700 dark:text-gray-300 line-clamp-2"
                              title={notification.subject}
                            >
                              {notification.subject}
                            </p>
                            {hasSmtpDetails && (
                              <div className="mt-2 space-y-0.5 rounded-lg bg-slate-50 px-2.5 py-2 text-[11px] text-slate-600 ring-1 ring-inset ring-slate-200 dark:bg-slate-800/60 dark:text-slate-300 dark:ring-slate-700">
                                {messageId && (
                                  <p className="truncate" title={messageId}>
                                    <span className="font-medium text-slate-700 dark:text-slate-200">Message ID:</span>{' '}
                                    {messageId}
                                  </p>
                                )}
                                {smtpServerResponse && (
                                  <p className="truncate" title={smtpServerResponse}>
                                    <span className="font-medium text-slate-700 dark:text-slate-200">SMTP:</span>{' '}
                                    {smtpServerResponse}
                                  </p>
                                )}
                                {acceptedList && (
                                  <p className="truncate text-emerald-700 dark:text-emerald-300" title={acceptedList}>
                                    <span className="font-medium">Accepted:</span> {acceptedList}
                                  </p>
                                )}
                                {rejectedList && (
                                  <p className="truncate text-red-700 dark:text-red-300" title={rejectedList}>
                                    <span className="font-medium">Rejected:</span> {rejectedList}
                                  </p>
                                )}
                                {pendingList && (
                                  <p className="truncate text-amber-700 dark:text-amber-300" title={pendingList}>
                                    <span className="font-medium">Pending:</span> {pendingList}
                                  </p>
                                )}
                              </div>
                            )}
                            {notification.errorMessage && (
                              <p
                                className="mt-1.5 text-xs text-red-600 dark:text-red-400"
                                title={notification.errorMessage}
                              >
                                {notification.errorMessage.length > 120
                                  ? `${notification.errorMessage.substring(0, 120)}...`
                                  : notification.errorMessage}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex shrink-0 flex-row items-center justify-between gap-3 sm:flex-col sm:items-end">
                          <div className="flex items-center gap-1.5 text-xs text-gray-500">
                            <ClockIcon className="h-3.5 w-3.5" />
                            {formatDate(notification.sentAt)}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setPreviewNotification(notification)}
                              className="gap-1.5"
                            >
                              <EyeIcon className="h-4 w-4" />
                              Preview
                            </Button>
                            {notification.status === 'failed' && isSystemEnabled && (
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => handleResend(notification.id)}
                                disabled={resendingId === notification.id}
                                className="gap-1.5"
                              >
                                <RefreshIcon className="h-4 w-4" />
                                {resendingId === notification.id ? 'Resending...' : 'Resend'}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </ModalContent>

        <ModalFooter className="justify-end">
          <Button onClick={onClose} variant="secondary">
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {previewNotification && (
        <Modal isOpen={!!previewNotification} onClose={() => setPreviewNotification(null)} size="xl">
          <ModalHeader onClose={() => setPreviewNotification(null)}>
            <div className="min-w-0 pr-4">
              <ModalTitle className="truncate">
                {previewNotification.subject || 'Email Preview'}
              </ModalTitle>
              <p className="mt-1 truncate text-xs text-gray-500">
                To: {previewNotification.recipient?.userDisplayName || previewNotification.recipientEmail}
                {' � '}
                {previewNotification.recipientEmail}
              </p>
            </div>
          </ModalHeader>
          <ModalContent maxHeight="80vh">
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-inner dark:border-gray-700">
              <iframe
                title="Email HTML Preview"
                sandbox="allow-same-origin"
                style={{ width: '100%', height: '70vh', border: 'none' }}
                srcDoc={previewNotification.body}
              />
            </div>
          </ModalContent>
          <ModalFooter className="justify-end">
            <Button onClick={() => setPreviewNotification(null)} variant="secondary">
              Close
            </Button>
          </ModalFooter>
        </Modal>
      )}

      <AlertDialog
        isOpen={showAlert.show}
        onClose={() => setShowAlert({ show: false, message: '', type: 'error' })}
        title=""
        message={showAlert.message}
        type={showAlert.type}
        confirmText="OK"
      />
    </>
  );
};

export default EmailNotificationHistoryModal;
